/* AXEL card behaviour (Andre, 2026-08-30; the design was named "Axel" on
   2026-08-31 -- the filename and the .cx2-* classes were left alone, because
   they are load-bearing and a mass rename would risk a working layout for a
   naming preference. Read `cards2` / `.cx2-` as "the Axel design").
   Only ever runs against .cx2 cards, so PDS1 is untouched whichever design the
   collection section is set to.

   Three jobs:
     1. hovering a colourway block swaps the card's shoe photograph
     2. clicking a size adds that variant straight to the cart
     3. the Women's / Men's control flips one attribute on <html>

   The add path is deliberately the SAME one assets/pulse-hp.js already uses on
   the PDP -- POST /cart/add.js, then re-read /cart.js for the true count and
   write it into [data-pulse-cart-count]. Reusing it means the cart bubble, the
   drawer re-render and the count carry-over all keep working; a second,
   different add path is how those quietly drift apart. */
(function () {
  'use strict';

  /* Included by every section that renders the Axel card (shop rail, best
     sellers, collection, search, the PDP rails), so this runs ONCE per page
     however many tags there are. The per-card work is idempotent through
     dataset.*Wired flags; the scale, density and resize listeners are not, and
     were binding twice on the homepage. */
  if (window.__pulse_cards2) return;
  window.__pulse_cards2 = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    /* Hover-only artwork must not compete with visible images on touch devices.
       On a mouse/trackpad, warm only cards approaching the viewport, after load. */
    var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
    var warmed = {};
    var hoverObserver = window.IntersectionObserver ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var urls = entry.target.__pulseHoverUrls || [];
        urls.forEach(warmImage);
        hoverObserver.unobserve(entry.target);
      });
    }, { rootMargin: '200px' }) : null;

    function warmImage(url) {
      if (!hoverMQ.matches || warmed[url]) return;
      warmed[url] = true;
      var image = new Image();
      image.fetchPriority = 'low';
      image.src = url;
    }

    function warmNearCard(card, url) {
      if (!hoverMQ.matches) return;
      (card.__pulseHoverUrls || (card.__pulseHoverUrls = [])).push(url);
      function observe() {
        if (hoverObserver) hoverObserver.observe(card);
        else card.addEventListener('mouseenter', function () { warmImage(url); }, { once: true });
      }
      if (document.readyState === 'complete') observe();
      else window.addEventListener('load', observe, { once: true });
    }
    /* ---------- 0. generic hover swap ----------
       Any element carrying data-hover-src swaps the <img> inside it to that
       shot while hovered. Used for the shoe-pointing-left shot on both the Axel
       card and the Final Sale rail, so the two cannot drift apart.

       srcset WINS OVER src, so it must be removed for a swap to be visible at
       all -- setting src alone looks like nothing happened on a responsive
       image. Both are stashed and restored on leave. */
    function hoverSwap(scope, getImg) {
      var img = getImg(scope);
      var url = scope.getAttribute('data-hover-src');
      if (!img || !url) return null;

      var original = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') };
      warmNearCard(scope, url);

      function show(u) {
        img.removeAttribute('srcset');
        img.setAttribute('src', u);
      }
      function restore() {
        img.setAttribute('src', original.src);
        if (original.srcset) img.setAttribute('srcset', original.srcset);
        else img.removeAttribute('srcset');
      }

      /* MOUSE ONLY (Andre, 2026-10-18). A phone fires mouseenter on a tap, so
         the swap started loading the hover shot just as the tap navigated to
         the PDP. The navigation aborted that load, the page went into the back
         cache with the image in its failed state, and Back showed a broken
         image on the very card that was tapped. */
      scope.addEventListener('mouseenter', function () { if (hoverMQ.matches) show(url); });
      scope.addEventListener('mouseleave', restore);
      swapped.push(restore);
      return { img: img, show: show, restore: restore, hover: url };
    }

    /* Whatever a swap left behind, a page restored from the back cache shows
       its resting photographs again. */
    var swapped = [];
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) swapped.forEach(function (fn) { fn(); });
    });

    document.querySelectorAll('[data-hover-src]').forEach(function (el) {
      if (el.dataset.hoverWired) return;
      el.dataset.hoverWired = '1';
      hoverSwap(el, function (s) { return s.querySelector('img'); });
    });

    /* ---------- 1. colourway hover swaps the shoe ---------- */
    document.querySelectorAll('[data-cx2]').forEach(function (cell) {
      if (cell.dataset.cx2Wired) return;
      cell.dataset.cx2Wired = '1';

      /* Found by CLASS, not a data attribute. `image_tag` cannot take a
         hyphenated named argument -- Liquid reads the hyphens as minus
         operators, which breaks the argument list and silently drops
         `class: 'cx-shoe'` too, leaving every card an empty grey box. */
      var shoe = cell.querySelector('.cx-wall__media img');
      if (shoe) {
        /* The card's hover shot now lives on the CELL, not the link. */
        var cardHover = cell.getAttribute('data-hover-src');
        var original = { src: shoe.getAttribute('src'), srcset: shoe.getAttribute('srcset') };

        cell.querySelectorAll('.cx2-col[data-cx2-img]').forEach(function (sw) {
          var url = sw.getAttribute('data-cx2-img');
          if (!url) return;
          warmNearCard(cell, url);

          sw.addEventListener('mouseenter', function () {
            if (!hoverMQ.matches) return;
            shoe.removeAttribute('srcset');
            shoe.setAttribute('src', url);
          });
          /* Back to the CARD's hover shot, not the resting one: the pointer is
             still inside the card, so the card is still hovered. Restoring the
             original here would make the shoe flick to three-quarter and then
             back to left-facing as the pointer left the swatch. */
          sw.addEventListener('mouseleave', function () {
            if (!hoverMQ.matches) return;
            if (cardHover) {
              shoe.removeAttribute('srcset');
              shoe.setAttribute('src', cardHover);
              return;
            }
            shoe.setAttribute('src', original.src);
            if (original.srcset) shoe.setAttribute('srcset', original.srcset);
            else shoe.removeAttribute('srcset');
          });
        });
      }

      /* ---------- 2. a size is an add to cart ---------- */
      cell.querySelectorAll('[data-cx2-add]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (!window.fetch) return;
          if (btn.dataset.busy) return;
          btn.dataset.busy = '1';
          btn.classList.add('is-adding');

          /* THE SIDE CART MUST BE RE-RENDERED, not just counted (Andre,
             2026-08-31: "the cart icon changes to one, but ... no product is
             appearing").

             The drawer's contents are server-rendered HTML sitting in the DOM
             from page load. Adding via /cart/add.js changes the CART but not
             that markup, so the bubble incremented off /cart.js while the drawer
             still showed its original, empty state. Nothing errored.

             Dawn's own answer, which product-form.js uses on the PDP: ask
             /cart/add.js to return the rendered sections, then hand the whole
             response to <cart-drawer>.renderContents(). Reusing the theme's
             method rather than rebuilding the drawer HTML here is the point --
             it also re-renders the cart bubble and opens the drawer, exactly as
             adding from a product page does. */
          var drawer = document.querySelector('cart-drawer');

          var body = new FormData();
          body.append('id', btn.getAttribute('data-cx2-add'));
          body.append('quantity', '1');
          /* Stamp the scale this size was CHOSEN in onto the line. The cart
             renders each line in its own scale from here on, so picking a
             men's 11 and then switching to women's for the next pair no longer
             relabels the first one (Andre, 2026-08-31). The underscore keeps it
             hidden from the customer at checkout and on the order. */
          var scale = window.PulseSizeScale.get();
          body.append('properties[_scale]', scale);
          /* And the same size in words, WITHOUT the underscore, so it shows on
             the checkout line, the order and the confirmation email. Shopify
             renders the variant's own "US M 9 / US W 10 / EU 42" above it and
             that cannot be replaced on a non-Plus plan, so this is a second
             line rather than a substitute -- deliberate, agreed 2026-08-31. */
          var label = btn.getAttribute(scale === 'm' ? 'data-label-m' : 'data-label-w');
          if (label) body.append('properties[Size]', label);
          /* A pre-order size carries its selling plan (data-cx2-plan, from the
             variant's market state), so the quick add is a real pre-order too. */
          var plan = btn.getAttribute('data-cx2-plan');
          if (plan) body.append('selling_plan', plan);
          if (drawer && drawer.getSectionsToRender) {
            body.append('sections', drawer.getSectionsToRender().map(function (sec) {
              return sec.id;
            }).join(','));
            body.append('sections_url', window.location.pathname);
          }

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: body
          }).then(function (r) {
            /* Parse defensively. A rate limit or the storefront's "Verifying
               your connection" interstitial answers with HTML, and r.json()
               would throw a SyntaxError that read as a network failure. A 422
               carries Shopify's own sentence in `description` ("...is already
               sold out"); keep it, and mark it as a refusal rather than a
               failure (audit JS-7). */
            return r.text().then(function (text) {
              var data = null;
              try { data = JSON.parse(text); } catch (e) { data = null; }
              if (!r.ok || !data) {
                var err = new Error((data && (data.description || data.message)) || ('add failed: ' + r.status));
                err.rejected = r.status === 422;
                throw err;
              }
              return data;
            });
          }).then(function (response) {
            btn.classList.remove('is-adding');
            btn.classList.add('is-added');

            if (drawer && drawer.renderContents && response && response.sections) {
              /* Re-renders the drawer AND the cart bubble, and opens the drawer.
                 Nothing else to fetch: the earlier read-back of /cart.js after
                 this point could fail on its own and navigated the customer
                 away from a bag that had just been filled. */
              /* Record the opener so the drawer's close() hands focus back to
                 it (audit A11Y-16). The size button itself is display:none, so
                 the card's quick-add cross is the element that can take focus. */
              if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(cell.querySelector('[data-cx2-quick]') || btn);
              drawer.renderContents(response);
              return null;
            }
            /* No drawer on the page, so no sections came back: keep the bubble
               honest, and never let this read-back fail the add. */
            return fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (cart) {
                if (cart && typeof cart.item_count === 'number') {
                  document.querySelectorAll('[data-pulse-cart-count]').forEach(function (n) {
                    n.textContent = cart.item_count;
                    n.hidden = cart.item_count === 0;
                  });
                }
              })
              .catch(function () { /* the add succeeded; a stale bubble is survivable */ });
          }).then(function () {
            setTimeout(function () { btn.classList.remove('is-added'); }, 1800);
          }).catch(function (err) {
            console.error('[pulse] cx2 add to cart failed:', err);
            btn.classList.remove('is-adding');
            if (err.rejected && drawer) {
              /* Shopify refused the add (sold out since the wall rendered, or a
                 variant it does not know). Say so in the cart's own alert
                 region, where the drawer's size-swap and add paths already put
                 their errors, and open the bag so it can be seen. Stay on the
                 page: the wall is still the right place to pick another size. */
              var box = document.getElementById('CartDrawer-CartErrors');
              if (box) box.textContent = err.message;
              if (typeof drawer.open === 'function') drawer.open(btn);
              return;
            }
            /* Anything else (network, interstitial): fall back to the product
               page, which owns the real picker. */
            var link = cell.querySelector('.cx-wall__link');
            if (link) window.location.href = link.getAttribute('href');
          }).then(function () {
            delete btn.dataset.busy;
          });
        });
      });
    });

    /* ---------- 3. the Women's / Men's scale switch ----------
       Storage, the <html> attribute and the default all belong to
       window.PulseSizeScale (snippets/pulse-size-scale.liquid), which runs
       render-blocking in <head> so the scale is right before first paint. This
       file's only job now is the buttons' pressed state -- and it does that on
       load AND on change, so a switch made on a product page is reflected here
       the moment the customer comes back. */
    function pressed(scale) {
      document.querySelectorAll('[data-cx2-scale]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-cx2-scale') === scale));
      });
    }
    pressed(window.PulseSizeScale.get());
    window.PulseSizeScale.onChange(pressed);

    document.querySelectorAll('[data-cx2-scale]').forEach(function (b) {
      b.addEventListener('click', function () {
        window.PulseSizeScale.set(b.getAttribute('data-cx2-scale'));
      });
    });

    /* ---------- 4. density: view by 2 / 4 / 8 ----------
       Writes --cols on the wall; the grid template reads it. CSS keeps the wall
       two-up below 700px whatever is chosen, so this cannot produce a phone
       showing eight cards across. */
    /* MOBILE AND DESKTOP KEEP SEPARATE PREFERENCES (Andre, 2026-09-01: the
       phone switcher offers 1 or 2). The switcher shows a different pair per
       breakpoint, so one shared key meant a desktop 4 arrived on a phone where
       no button offers 4 and nothing read as pressed. Two keys means each
       breakpoint can only ever store a value it can also undo, which is the
       same invariant the `allowed` check below was written to protect.
       699 matches the CSS breakpoint exactly; changing one means changing both. */
    var COLS = 'pulse-wall-cols';
    var COLS_M = 'pulse-wall-cols-m';
    var phoneMQ = window.matchMedia('(max-width: 699px)');
    function colsKey() { return phoneMQ.matches ? COLS_M : COLS; }
    function colsDefault() { return phoneMQ.matches ? '2' : '4'; }
    var wall = document.querySelector('.cx2-wall');
    var viewBtns = document.querySelectorAll('[data-cx2-cols]');

    /* Keep `sizes` honest, and oversample.

       The resting image is responsive (srcset + sizes) while the hover shot is
       a single fixed-width file, so at 1x the browser was picking a candidate
       almost exactly equal to the CSS box -- a 1:1 render with no headroom --
       while hover always got the full-width file downscaled ~3x into the same
       box. A downscaled image resamples crisper than a 1:1 one, which is why
       the resting shoe read soft and the hover shoe read sharp.

       Two fixes in one: `sizes` is set from the MEASURED card width rather than
       a hardcoded vw guess (which the density switcher would invalidate the
       moment the column count changed), and it asks for 2x that width so the
       wall is retina-quality on any display. */
    function syncSizes() {
      var media = document.querySelector('.cx2 .cx-wall__media');
      if (!media) return;
      var px = Math.ceil(media.getBoundingClientRect().width * 2);
      if (!px) return;
      document.querySelectorAll('.cx2 .cx-wall__media img').forEach(function (i) {
        i.setAttribute('sizes', px + 'px');
      });
    }

    function applyCols(n) {
      if (!wall) return;
      wall.style.setProperty('--cols', n);
      wall.setAttribute('data-cols', n);   /* CSS hides the size run at 8-up */
      /* after the grid has re-laid out, not before */
      requestAnimationFrame(syncSizes);
      viewBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-cx2-cols') === String(n)));
      });
      try { localStorage.setItem(colsKey(), String(n)); } catch (e) { /* private mode */ }
    }

    /* Read THIS breakpoint's key and apply it, falling back to that
       breakpoint's default. Called again when the viewport crosses 700, so a
       resize picks up the preference belonging to the width it landed on. */
    function restoreCols() {
      var savedCols = null;
      try { savedCols = localStorage.getItem(colsKey()); } catch (e) { /* private mode */ }
      /* Only honour a value the switcher actually offers, so a stale or hand-set
         key cannot leave the wall on a column count with no button to undo it. */
      var allowed = [].slice.call(viewBtns).map(function (b) { return b.getAttribute('data-cx2-cols'); });
      applyCols(savedCols && allowed.indexOf(savedCols) !== -1 ? savedCols : colsDefault());
    }

    if (wall && viewBtns.length) {
      restoreCols();
      if (phoneMQ.addEventListener) phoneMQ.addEventListener('change', restoreCols);
      else if (phoneMQ.addListener) phoneMQ.addListener(restoreCols);

      viewBtns.forEach(function (b) {
        b.addEventListener('click', function () { applyCols(b.getAttribute('data-cx2-cols')); });
      });
    }

    /* ---------- 5. SAVE (per-browser wishlist) ----------
       localStorage, not the customer account: there is no saved-items page and
       no account sync yet, so this remembers on THIS device and says so. The
       store is isolated here, so swapping it for an account-backed one later
       touches nothing else. Every read and write is guarded -- a private window
       throws on access rather than returning null. */
    var SAVED = 'pulse-saved';
    function readSaved() {
      /* WP4-D (2026-09-06): the same Array.isArray guard its twin in pulse-saved.js carries.
         The key is shared, so anything that ever writes a non-array under it -- an older
         format, another tab, a hand edit -- reached `.indexOf` on a non-array here and threw,
         killing the click handler for every save button on the page. */
      try {
        var list = JSON.parse(localStorage.getItem(SAVED) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) { return []; }
    }
    function writeSaved(list) {
      try { localStorage.setItem(SAVED, JSON.stringify(list)); } catch (e) { /* private mode */ }
    }
    var savedIds = readSaved();
    document.querySelectorAll('[data-cx2-save]').forEach(function (btn) {
      if (btn.dataset.wired) return;
      btn.dataset.wired = '1';
      var id = btn.getAttribute('data-cx2-save');
      if (savedIds.indexOf(id) !== -1) btn.setAttribute('aria-pressed', 'true');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        savedIds = readSaved();
        var i = savedIds.indexOf(id);
        if (i === -1) { savedIds.push(id); btn.setAttribute('aria-pressed', 'true'); }
        else { savedIds.splice(i, 1); btn.setAttribute('aria-pressed', 'false'); }
        writeSaved(savedIds);
        /* ANNOUNCE IT. assets/pulse-saved.js has always listened for this event
           to repaint the nav heart's count, and nothing ever fired it from a
           card -- only the panel's own remove button did. So saving from a card
           was silent: the badge stayed empty until the panel was opened, which
           runs syncCount() on the way in (Andre, 2026-08-31 21:37: "it doesn't
           register a one unless I open the wish list and then close it").

           A `storage` event is not a substitute. It fires only in OTHER tabs,
           never in the one that wrote, which is precisely the tab that needs
           repainting here. */
        document.dispatchEvent(new CustomEvent('pulse:saved-changed'));
      });
    });

    /* ---------- 6. NOTIFY (back in stock) ----------
       Posts to Klaviyo's CLIENT API, which authenticates with the PUBLIC company id only. The
       private KLAVIYO_API_KEY must never reach a browser and is not used here.

       WP4-D (2026-09-06). This used to POST Klaviyo's NATIVE
       /client/back-in-stock-subscriptions/, which is MARKET-BLIND: that trigger watches ONE
       global inventory number, so an Australian restock would email US customers, and the call
       carried none of the pulse_* stamps the rest of the site sends. The PDP dialog
       (assets/pulse-back-in-stock.js) already does the right thing and explains why in its own
       comment. This path now raises the SAME custom "Back in Stock Request" event with the SAME
       properties, so ~/pulse-project/klaviyo/back_in_stock_notifier.py picks a card request up
       exactly as it picks a PDP one up. There must be no call to back-in-stock-subscriptions
       from anywhere on this site. */
    var kEl = document.querySelector('[data-klaviyo-id]');
    var KLAVIYO_ID = kEl ? kEl.getAttribute('data-klaviyo-id') : (window.PulseKlaviyoId || null);

    /* THE MARKET, and where it comes from on a collection page. The card snippet carries no
       market attributes and is not this package's to change, and there is no other
       server-rendered market value in the DOM on a collection page -- so the footer's
       newsletter script, which already computes exactly this vocabulary in Liquid, publishes
       it as window.PulseMarket and this reads it. One Liquid `case`, no second copy of the
       mapping in JS. The footer is in the footer group and renders on every storefront page;
       if it somehow has not, the domain is still a true market discriminator on production
       (each market has its own domain), so `domain` is always sent and the handle is left
       empty rather than guessed at. */
    /* Visitor-language strings (2026-10-21): layout/theme.liquid publishes pulse_ui.js as
       window.PulseUI. English is the fallback when it is missing. Values are escaped because
       they go into innerHTML. */
    function ui(k, en) {
      var v = (window.PulseUI && typeof window.PulseUI[k] === 'string') ? window.PulseUI[k] : en;
      /* Liquid's `t` HTML-escapes ("l&#39;alerte"), so decode first, then escape once. */
      v = String(v).replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
      return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function mkt() {
      var m = window.PulseMarket || {};
      return {
        code: m.code || '',
        handle: m.handle || '',
        domain: m.domain || location.host
      };
    }

    document.querySelectorAll('[data-cx2-notify]').forEach(function (btn) {
      if (btn.dataset.wired) return;
      btn.dataset.wired = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var cell = btn.closest('[data-cx2]');
        if (!cell || cell.querySelector('.cx2-notify')) return;

        var vid = btn.getAttribute('data-cx2-notify');
        var box = document.createElement('div');
        box.className = 'cx2-notify';
        box.innerHTML =
          '<input type="email" inputmode="email" autocomplete="email" placeholder="' + ui('email_placeholder', 'Email address') + '" aria-label="' + ui('email_aria', 'Email for back in stock alert') + '">' +
          '<button type="button">' + ui('notify_me', 'Notify me') + '</button>';
        cell.appendChild(box);
        var input = box.querySelector('input');
        var go = box.querySelector('button');
        input.focus();

        function say(msg) { box.innerHTML = '<span class="cx2-notify__msg">' + msg + '</span>'; }

        go.addEventListener('click', function () {
          var email = (input.value || '').trim();
          if (!email || email.indexOf('@') === -1) { input.focus(); return; }
          if (!KLAVIYO_ID) { say(ui('bis_off', 'Back in stock alerts are not switched on yet.')); return; }
          go.disabled = true;

          var m = mkt();
          var scaleNow = (window.PulseSizeScale && window.PulseSizeScale.gender) ? window.PulseSizeScale.gender() : 'women';
          var link = cell.querySelector('a[href]');
          var path = '';
          try { path = link ? new URL(link.getAttribute('href'), location.origin).pathname : ''; } catch (err) { path = ''; }
          var name = btn.getAttribute('data-cx2-name') || '';
          var sizeWords = btn.getAttribute(scaleNow === 'men' ? 'data-label-m' : 'data-label-w') || '';
          /* Only a real product URL yields a handle. `replace` returns the subject UNCHANGED
             when it does not match, so a card whose link is a collection or a page would have
             sent its whole path as the handle and the notifier would have matched nothing.
             Test for the segment first and send '' when it is absent. */
          var handle = '';
          var pIdx = path.indexOf('/products/');
          if (pIdx > -1) handle = path.slice(pIdx + 10).split('/')[0];

          /* The SAME shape assets/pulse-back-in-stock.js sends. Property NAMES are copied from
             that file deliberately: the notifier matches on them, so a card request and a PDP
             request have to be indistinguishable to it. */
          var props = {
            pulse_source: 'back-in-stock',
            pulse_market: m.code,
            pulse_domain: m.domain,
            pulse_gender: scaleNow,
            pulse_bis_product: name,
            pulse_bis_handle: handle,
            pulse_bis_size: sizeWords,
            pulse_bis_variant: vid
          };
          var request = { data: { type: 'event', attributes: {
            properties: {
              variant_id: vid,
              product_id: '',
              product_name: name,
              handle: handle,
              size_label: sizeWords,
              market: m.code,
              market_handle: m.handle,
              domain: m.domain,
              gender: scaleNow,
              url: 'https://' + m.domain + path + (vid ? '?variant=' + vid : '')
            },
            metric: { data: { type: 'metric', attributes: { name: 'Back in Stock Request' } } },
            profile: { data: { type: 'profile', attributes: { email: email, properties: props } } }
          } } };

          fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(KLAVIYO_ID), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
            body: JSON.stringify(request)
          }).then(function (r) {
            if (!r.ok) throw new Error('klaviyo ' + r.status);
            say(ui('bis_thanks', 'Thanks. We will email you when this size is back.'));
          }).catch(function (err) {
            console.error('[pulse] back-in-stock signup failed:', err);
            say(ui('bis_failed', 'That did not go through. Please try again shortly.'));
          });
        });

        input.addEventListener('keydown', function (ev) { if (ev.key === 'Enter') go.click(); });
        /* Leaving the card closes it, so an open form cannot be left behind on a card the
           customer has moved away from -- but WP4-D (2026-09-06): `{ once: true }` fired on the
           FIRST mouseleave whatever the customer was doing, so a half-typed address vanished the
           moment the pointer crossed the card edge (which it does on the way to anything else,
           and on a trackpad flick it does not even need to). Only close an EMPTY, unfocused box,
           and stay armed for the next mouseleave rather than burning the listener on one.

           Bound ONCE per cell, not once per open: without `{ once: true }` a listener added on
           every Notify click would pile up on a card the customer opens and closes repeatedly.
           It resolves the current box each time, so it needs no closure over this one. */
        if (!cell.dataset.cx2NotifyLeave) {
          cell.dataset.cx2NotifyLeave = '1';
          cell.addEventListener('mouseleave', function () {
            var open = cell.querySelector('.cx2-notify');
            if (!open) return;
            var live = open.querySelector('input');
            if (!live) return;                                 /* already submitted: the message stands */
            if (document.activeElement === live) return;       /* they are typing in it */
            if ((live.value || '').trim()) return;             /* they have typed something */
            open.remove();
          });
        }
      });
    });

    /* Also on first paint and on resize, so a card that is not 4-up by default
       -- or a window that changes width -- is never left asking for too few
       pixels. Debounced: `sizes` only needs to be right once the resize settles. */
    syncSizes();
    var rt;
    addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(syncSizes, 200);
    });
  });
})();
